/* eslint-disable complexity, max-lines-per-function */
import type { ComponentProps } from 'react';
import React from 'react';
import type { SignupFlow, StringMap } from 'corgi-types';

import Alert from 'common/components/Alert';
import AutoScrollAlert from 'common/components/AutoScrollAlert';
import { Flex } from 'common/components/Flex';
import { BodyRegularBR1, HeadlineH5 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useTranslatedErrorMessageState } from 'common/hooks/form/error';
import { useReevaluateCartDiscounts } from 'common/hooks/home-club/reevaluate-discounts';
import { useRedirectAfterDelay } from 'common/hooks/signup';
import { useLocalization } from 'common/providers/LocaleProvider';
import { Event } from 'common/types/auth';
import CheckoutHomeClubModal from '../../cart-summary/components/CtaButtons/CheckoutHomeClubModal';
import CtaCheckoutGuest from './CtaButtons/CtaCheckoutGuest';
import CtaRegister from './CtaButtons/CtaRegister';
import Form from './Form';
import Separator from './ThirdParty/Separator';
import ThirdPartyLoginSection from './ThirdParty/ThirdPartyLoginSection';
import { useHomeClubModal } from './utils/homeclub-modal';

interface LoginFormProps {
  userFlow: SignupFlow;
  appleClientId: string;
  withAppleFirst: boolean;
  appleStateToken: string;
  withGuestLogin: boolean;
  referredBy?: string;
  successRedirectUrl?: string;
  withHomeClubSubscriptionModal?: boolean;
  discountPendingTotal?: number;
}

interface LoginFormSuccessProps {
  queryParams?: StringMap;
}

interface CardProps extends ComponentProps<typeof Flex> {
  children: React.ReactNode;
}

const Card = ({ children, ...styleProps }: CardProps) => (
  <Flex
    flexDirection="column"
    py={['sp_16', null, null, 'sp_20']}
    px={['sp_12', 'sp_16', null, 'sp_24']}
    backgroundColor="sys.neutral.background.default"
    borderRadius="medium"
    boxSizing="border-box"
    width="100%"
    {...styleProps}
  >
    {children}
  </Flex>
);

const LoginForm = ({
  userFlow,
  referredBy,
  appleClientId,
  withAppleFirst,
  withGuestLogin,
  appleStateToken,
  successRedirectUrl,
  discountPendingTotal: initialDiscountPendingTotal,
  withHomeClubSubscriptionModal = false,
}: LoginFormProps) => {
  const { t, r, fc } = useLocalization();
  const [commonError, setCommonError] = useTranslatedErrorMessageState();
  const reevaluateCartDiscounts = useReevaluateCartDiscounts();

  const onSuccessRedirect = useRedirectAfterDelay(
    successRedirectUrl ? { redirectUrl: successRedirectUrl } : { userFlow }
  );

  const { isHomeClubModalOpen, discountPendingTotal, checkAndShowHomeClubModal } = useHomeClubModal(
    {
      setCommonError,
      initialDiscountPendingTotal,
    }
  );

  const handleLoginSuccess = async ({ queryParams }: LoginFormSuccessProps = {}) => {
    if (withHomeClubSubscriptionModal) {
      const isModalShown = await checkAndShowHomeClubModal();

      if (isModalShown) {
        return;
      }
    }

    onSuccessRedirect({ queryParams });
  };

  const handleGuestCheckout = withHomeClubSubscriptionModal
    ? async () => {
        await reevaluateCartDiscounts();
      }
    : undefined;

  return (
    <div data-corgi-id="login-form">
      {commonError && (
        <AutoScrollAlert
          mb="sp_12"
          mt="sp_0"
          message={commonError.message}
          messageId={commonError.timestamp}
        />
      )}

      <Card>
        <HeadlineH5 as="h1" mt="sp_0" mb="sp_16">
          {t(withHomeClubSubscriptionModal ? 'h24_login_form_hc_heading' : 'h24_register')}
        </HeadlineH5>

        <Form
          setCommonError={setCommonError}
          onSuccess={handleLoginSuccess}
          userFlow={userFlow}
          withAutoFocus={false}
        />

        <ThirdPartyLoginSection
          userFlow={userFlow}
          appleClientId={appleClientId}
          appleStateToken={appleStateToken}
          setCommonError={setCommonError}
          onSuccess={handleLoginSuccess}
          referredBy={referredBy}
          withSeparator={false}
          withAppleFirst={withAppleFirst}
        />

        {!withHomeClubSubscriptionModal && <Separator my="sp_16" />}

        {withGuestLogin && !withHomeClubSubscriptionModal && (
          <CtaCheckoutGuest userFlow={userFlow} mb="sp_12" onClick={handleGuestCheckout} />
        )}

        <CtaRegister
          kind="secondary-v2"
          label={t('h24_registration_submit_button')}
          userFlow={userFlow}
          referredBy={referredBy}
          mt={withHomeClubSubscriptionModal ? 'sp_12' : 'sp_0'}
        />
      </Card>

      {withGuestLogin && withHomeClubSubscriptionModal && (
        <Card mt={['sp_20', null, null, 'sp_16']}>
          <HeadlineH5 as="h2" mb={['sp_12', null, null, 'sp_16']}>
            {t('h24_continue_without_hc_coupon')}
          </HeadlineH5>

          {discountPendingTotal && (
            <Alert type={MessageType.Warning} mb="sp_16">
              <BodyRegularBR1
                color="sys.warning.text.strong"
                dangerouslySetInnerHTML={{
                  __html: r.t('h24_hc_coupons_guest_warning', {
                    discount: fc(discountPendingTotal),
                  }),
                }}
              />
            </Alert>
          )}

          <CtaCheckoutGuest userFlow={userFlow} onClick={handleGuestCheckout} />
        </Card>
      )}

      {withHomeClubSubscriptionModal && discountPendingTotal && (
        <CheckoutHomeClubModal
          isModalOpen={isHomeClubModalOpen}
          homeClubDiscount={discountPendingTotal}
          eventName={Event.LoginFormClick}
        />
      )}
    </div>
  );
};

export default LoginForm;
